/**
 * De weergavekeuze van het huishoudenoverzicht: tabel, bord of agenda.
 *
 * **In de url en niet in Firestore.** Dit is een keuze van één persoon op één
 * moment, geen instelling van de organisatie. In de url blijft hij deelbaar, en
 * de deeplink vanaf de dashboardkaart (`?status=…`) blijft naast deze parameter
 * gewoon werken.
 *
 * AI-CONTEXT: See docs/features/household-board-view.md
 */

/** @type {'board'} */
const BOARD_VIEW = 'board'

/** @type {'agenda'} */
const AGENDA_VIEW = 'agenda'

/** @type {'table'} */
const TABLE_VIEW = 'table'

/**
 * De weergaven die als parameter in de url staan. De tabel hoort hier NIET bij:
 * dat is de standaard, en die laat de url leeg.
 *
 * Eén lijst, zodat een nieuwe weergave aan beide kanten tegelijk meetelt --
 * lezen en schrijven. Twee losse lijstjes lopen uiteen zodra er een derde
 * bijkomt, en dan schrijft de schakelaar een waarde die de lezer niet kent en
 * val je stil terug op de tabel.
 */
const PARAM_VIEWS = [BOARD_VIEW, AGENDA_VIEW]

/** De naam van de parameter in de url. */
const PARAM = 'view'

/**
 * Of 'Huishoudens inplannen' (de lijst huishoudens naast de agenda) er voor
 * deze organisatie is: alleen met de module Huishoudens — 'FIXtool' in het
 * abonnement, dezelfde vlag als het menu-item 'Huishoudens'. Eén poort voor
 * alle drie de ingangen (weergaveknop bij de tabel, knop 'huishoudens' in de
 * agenda, icoon op de dashboardkaart) én voor de url, zodat ze niet uiteen
 * kunnen lopen. Strikt `=== true`: 'nog niet geladen' is dicht.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * @param {{housevisits?: boolean}} [settings] - Uit `useSettings()`
 * @returns {boolean}
 */
const householdPlanningEnabled = (settings) => settings?.housevisits === true

/**
 * Leest de weergave uit een zoekreeks.
 *
 * Alles wat geen letterlijke `board` of `agenda` is valt terug op de tabel: dat
 * is de weergave die er altijd was, dus een oude link of een link waarin iemand
 * met de hand geknipt heeft levert wat de gebruiker gewend is — geen leeg
 * scherm. Er wordt niet getrimd of genormaliseerd; `Board` en ` board ` zijn
 * geen geldige waarden en horen dus ook niet stiekem te werken.
 *
 * Zonder de module Huishoudens is `agenda` ook zo'n link naar iets dat er niet
 * is: dan de tabel. De pagina geeft die poort mee; zonder opties doet deze
 * functie alsof de module aanstaat (de pure lezing van de url).
 *
 * @param {string} [search] - `location.search`, met of zonder vraagteken
 * @param {object} [opties]
 * @param {boolean} [opties.householdPlanning=true] - Of 'Lijst en agenda' voor deze organisatie bestaat
 * @returns {'board'|'agenda'|'table'}
 */
const resolveBoardView = (search, { householdPlanning = true } = {}) => {
    const params = new URLSearchParams(search || '')
    const gevraagd = params.get(PARAM)
    if (!PARAM_VIEWS.includes(gevraagd)) return TABLE_VIEW
    if (gevraagd === AGENDA_VIEW && !householdPlanning) return TABLE_VIEW
    return gevraagd
}

/**
 * De nieuwe zoekreeks wanneer iemand van weergave wisselt.
 *
 * Houdt de rest van de url heel — de statuskeuze staat er ook in, en die mag
 * niet sneuvelen bij een klik op een icoontje. Terug naar de tabel haalt de
 * parameter juist weg: dat is de standaard, en een url hoort niets te dragen
 * wat niets toevoegt.
 *
 * @param {string} [search] - De huidige `location.search`
 * @param {'board'|'agenda'|'table'} view - Waar naartoe gewisseld wordt
 * @returns {string} Zoekreeks inclusief vraagteken, of '' als er niets overblijft
 */
const boardViewSearch = (search, view) => {
    const params = new URLSearchParams(search || '')

    if (PARAM_VIEWS.includes(view)) {
        params.set(PARAM, view)
    } else {
        params.delete(PARAM)
    }

    const tekst = params.toString()
    return tekst === '' ? '' : `?${tekst}`
}

/**
 * Het pad naar 'Huishoudens naast de agenda' voor een organisatie — de
 * ingang vanaf de Agenda-pagina en de dashboardkaart (verzoek BuurtKlusBedrijf
 * 2026-09-28: 'losmaken van de tabel, openen vanaf meerdere plekken').
 *
 * Het padsegment komt uit de routetabel (`Navigation.js`: `housevisitsoverview`),
 * niet uit de naam van de map. De eerste versie zei 'adresses' en opende een wit
 * scherm zonder fout: de router tekent niets bij een onbekend pad. De test
 * ernaast leest de routetabel en wordt rood als dit segment daar niet in staat.
 *
 * @param {string} client - CompagnyID uit de url
 * @returns {string} bijv. '/abc/housevisitsoverview?view=agenda'
 */
const HOUSEHOLD_OVERVIEW_SEGMENT = 'housevisitsoverview'
const householdAgendaPath = (client) => `/${client}/${HOUSEHOLD_OVERVIEW_SEGMENT}${boardViewSearch('', AGENDA_VIEW)}`

export { AGENDA_VIEW, BOARD_VIEW, TABLE_VIEW, PARAM_VIEWS, boardViewSearch, householdAgendaPath, householdPlanningEnabled, HOUSEHOLD_OVERVIEW_SEGMENT }
export default resolveBoardView
